/**
 * Derives a coherent background color for the text side of slides using the
 * "Half image" slideshow layout. For each slide, samples pixels from the edge
 * of the image nearest the content area, softens the result toward white for
 * text legibility, and exposes it to CSS via the --half-image-bg custom
 * property on the slide's .slideshow__content element.
 */
(function () {
  if (typeof window === 'undefined' || typeof document === 'undefined') return;

  var SAMPLE_SIZE = 120;
  var SAMPLE_BAND = 0.45;
  var TARGET_LIGHTNESS = 0.86;
  var SATURATION_BOOST = 1.6;
  var MAX_SATURATION = 0.55;
  var ALPHA_THRESHOLD = 200;

  function sampleEdgeColor(img, side) {
    try {
      var canvas = document.createElement('canvas');
      var w = (canvas.width = SAMPLE_SIZE);
      var h = (canvas.height = SAMPLE_SIZE);
      var ctx = canvas.getContext('2d', { willReadFrequently: true });
      if (!ctx) return null;
      ctx.drawImage(img, 0, 0, w, h);
      var data = ctx.getImageData(0, 0, w, h).data;

      var startX = side === 'right' ? 0 : Math.floor(w * (1 - SAMPLE_BAND));
      var endX = side === 'right' ? Math.floor(w * SAMPLE_BAND) : w;

      var r = 0;
      var g = 0;
      var b = 0;
      var count = 0;
      for (var y = 0; y < h; y++) {
        for (var x = startX; x < endX; x++) {
          var i = (y * w + x) * 4;
          if (data[i + 3] < ALPHA_THRESHOLD) continue;
          r += data[i];
          g += data[i + 1];
          b += data[i + 2];
          count++;
        }
      }
      if (count === 0) return null;
      return [Math.round(r / count), Math.round(g / count), Math.round(b / count)];
    } catch (err) {
      return null;
    }
  }

  function rgbToHsl(rgb) {
    var r = rgb[0] / 255;
    var g = rgb[1] / 255;
    var b = rgb[2] / 255;
    var max = Math.max(r, g, b);
    var min = Math.min(r, g, b);
    var l = (max + min) / 2;
    var h;
    var s;
    if (max === min) {
      h = 0;
      s = 0;
    } else {
      var d = max - min;
      s = l > 0.5 ? d / (2 - max - min) : d / (max + min);
      switch (max) {
        case r: h = ((g - b) / d + (g < b ? 6 : 0)) / 6; break;
        case g: h = ((b - r) / d + 2) / 6; break;
        default: h = ((r - g) / d + 4) / 6;
      }
    }
    return [h, s, l];
  }

  function hslToRgb(hsl) {
    var h = hsl[0];
    var s = hsl[1];
    var l = hsl[2];
    if (s === 0) {
      var v = Math.round(l * 255);
      return [v, v, v];
    }
    var hue2rgb = function (p, q, t) {
      if (t < 0) t += 1;
      if (t > 1) t -= 1;
      if (t < 1 / 6) return p + (q - p) * 6 * t;
      if (t < 1 / 2) return q;
      if (t < 2 / 3) return p + (q - p) * (2 / 3 - t) * 6;
      return p;
    };
    var q = l < 0.5 ? l * (1 + s) : l + s - l * s;
    var p = 2 * l - q;
    return [
      Math.round(hue2rgb(p, q, h + 1 / 3) * 255),
      Math.round(hue2rgb(p, q, h) * 255),
      Math.round(hue2rgb(p, q, h - 1 / 3) * 255),
    ];
  }

  function harmonizeForBackground(rgb) {
    var hsl = rgbToHsl(rgb);
    var s = Math.min(MAX_SATURATION, hsl[1] * SATURATION_BOOST);
    return hslToRgb([hsl[0], s, TARGET_LIGHTNESS]);
  }

  function applyToSlide(slide) {
    if (slide.dataset.halfImageBgApplied === 'true') return;
    var img = slide.querySelector('.slideshow__bg img');
    var content = slide.querySelector('.slideshow__content');
    if (!img || !content) return;

    var run = function () {
      var side = slide.getAttribute('data-image-position') || 'left';
      var rgb = sampleEdgeColor(img, side);
      if (!rgb) return;
      var soft = harmonizeForBackground(rgb);
      content.style.setProperty(
        '--half-image-bg',
        'rgb(' + soft[0] + ', ' + soft[1] + ', ' + soft[2] + ')'
      );
      slide.dataset.halfImageBgApplied = 'true';
    };

    if (img.complete && img.naturalWidth > 0) {
      run();
    } else {
      img.addEventListener('load', run, { once: true });
      img.addEventListener('error', function () {
        slide.dataset.halfImageBgApplied = 'failed';
      }, { once: true });
    }
  }

  function processAll() {
    var slides = document.querySelectorAll(
      '.slideshow-layout--half-image .slideshow-slide--half-image'
    );
    slides.forEach(applyToSlide);
  }

  function watchForLazyLoads() {
    var wrappers = document.querySelectorAll('.slideshow-layout--half-image');
    if (!wrappers.length || typeof MutationObserver === 'undefined') return;
    var observer = new MutationObserver(function () {
      processAll();
    });
    wrappers.forEach(function (wrapper) {
      observer.observe(wrapper, { childList: true, subtree: true, attributes: true, attributeFilter: ['src', 'srcset'] });
    });
  }

  function init() {
    processAll();
    watchForLazyLoads();
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
})();
